/* eslint-disable prefer-arrow-callback */
import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback, useEffect, useMemo, useRef } from "react";

import { useReplaceSoilSightCropYears, useTranslation, useUpdateSoilSightProjectSettingsRefinements } from "utils";

import { SOIL_SIGHT_AUTOSAVE_DELAY_MS, SOIL_SIGHT_MAX_FLUSH_PASSES } from "consts";

import type { UpdateSettingsRefinementsBody } from "models";

import { useIsSavingSoilSight } from "pages/app/scenario-analysis/hooks/useIsSavingSoilSight";
import {
  isRowUnsaved,
  useSoilSightDebouncedCropYearsAutosave,
  useSoilSightDebouncedScenarioMetaAutosave,
  useSoilSightDebouncedSettingsAutosave,
} from "pages/app/scenario-analysis/hooks/useSoilSightAutosave";
import { type ProjectRefinementsState, type ScenarioData, type ScenarioSettings } from "pages/app/scenario-analysis/types/scenario";
import { watchProjectSwitch } from "pages/app/scenario-analysis/utils/projectSwitch";
import {
  type AutosaveRefs,
  isApiScenarioId,
  showSoilSightSaveFailed,
  toCropYearsPayload,
  waitUntilIdle,
} from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";
import { toApiSettingsRefinements } from "pages/app/scenario-analysis/utils/scenarioApiMappers";
import { withEmptyYearsPruned } from "pages/app/scenario-analysis/utils/scenarioBuilderState";

const toScenarioMetaRows = (scenarios: ScenarioData[]) => scenarios.map((scenario) => ({ id: scenario.id, body: { name: scenario.title } }));

const toCropYearsRows = (scenarios: ScenarioData[]) => scenarios.map((scenario) => ({ id: scenario.id, body: toCropYearsPayload(scenario.years) }));

type SoilSightPersistenceArgs = {
  isDirty: boolean;
  soilSightProjectId: string | null;
  scenarios: ScenarioData[];
  setScenarios: Dispatch<SetStateAction<ScenarioData[]>>;
  settings: ScenarioSettings;
  projectRefinements: ProjectRefinementsState;
  ensureProjectId: () => Promise<string | null>;
  projectEpochRef: MutableRefObject<number>;
  /** Last saved payloads and the hydration flag, shared with hydration and the project session. */
  autosaveRefs: AutosaveRefs;
  onScenarioMetaSaved?: (scenarioId: string) => void;
  onCropYearsSaved?: (scenarioId: string) => void;
};

/** Every write of the page's edits: the debounced autosaves, an immediate settings save, the flush before a run, and blank-row pruning. */
export const useSoilSightPersistence = ({
  isDirty,
  soilSightProjectId,
  scenarios,
  setScenarios,
  settings,
  projectRefinements,
  ensureProjectId,
  projectEpochRef,
  autosaveRefs,
  onScenarioMetaSaved,
  onCropYearsSaved,
}: SoilSightPersistenceArgs) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { hasHydratedRef, cropYearsSavedRef, scenarioMetaSavedRef, settingsSavedRef } = autosaveRefs;
  const { mutateAsync: updateSettingsAsync } = useUpdateSoilSightProjectSettingsRefinements();
  const { mutateAsync: replaceCropYearsAsync } = useReplaceSoilSightCropYears();
  const { isSavingCount, isSavingRef } = useIsSavingSoilSight();

  const settingsPayload = useMemo(() => toApiSettingsRefinements({ settings, projectRefinements }), [settings, projectRefinements]);
  // The flush runs between renders, so it reads the latest values (or ones set ahead of the render that shows them).
  const settingsPayloadRef = useRef(settingsPayload);
  settingsPayloadRef.current = settingsPayload;
  const scenariosRef = useRef(scenarios);
  scenariosRef.current = scenarios;
  const soilSightProjectIdRef = useRef(soilSightProjectId);
  soilSightProjectIdRef.current = soilSightProjectId;

  // Leaving the page saves edits still in their debounce window; before hydration the ledgers don't reflect the server yet.
  const isHydratedOnUnmount = useCallback(
    function isHydratedOnUnmount() {
      return hasHydratedRef.current;
    },
    [hasHydratedRef]
  );

  const scenarioMetaRows = useMemo(() => toScenarioMetaRows(scenarios), [scenarios]);
  const cropYearsRows = useMemo(() => toCropYearsRows(scenarios), [scenarios]);

  useSoilSightDebouncedSettingsAutosave({
    isDirty,
    payload: settingsPayload,
    delay: SOIL_SIGHT_AUTOSAVE_DELAY_MS.settings,
    ensureProjectId,
    settingsSavedRef,
    flushOnUnmount: isHydratedOnUnmount,
    t,
  });
  const saveChangedScenarioMeta = useSoilSightDebouncedScenarioMetaAutosave({
    isDirty,
    soilSightProjectId,
    hasHydratedRef,
    rows: scenarioMetaRows,
    delay: SOIL_SIGHT_AUTOSAVE_DELAY_MS.scenarioMeta,
    savedRef: scenarioMetaSavedRef,
    flushOnUnmount: isHydratedOnUnmount,
    t,
    onRowSaved: onScenarioMetaSaved,
  });
  const saveChangedCropYears = useSoilSightDebouncedCropYearsAutosave({
    isDirty,
    soilSightProjectId,
    hasHydratedRef,
    rows: cropYearsRows,
    delay: SOIL_SIGHT_AUTOSAVE_DELAY_MS.cropYears,
    savedRef: cropYearsSavedRef,
    flushOnUnmount: isHydratedOnUnmount,
    t,
    onRowSaved: onCropYearsSaved,
  });

  /** The next flush sends this payload, before the render that would show it. */
  const setPendingSettingsPayload = useCallback(function setPendingSettingsPayload(body: UpdateSettingsRefinementsBody) {
    settingsPayloadRef.current = body;
  }, []);

  /** Saves the settings now; resolves false when there is no project or the user switched project meanwhile. Throws on failure. */
  const saveSettingsNow = useCallback(
    async function saveSettingsNow(body: UpdateSettingsRefinementsBody, { klimFarmId }: { klimFarmId?: string } = {}): Promise<boolean> {
      const isSameProject = watchProjectSwitch(projectEpochRef);
      const payloadBeforeSave = settingsPayloadRef.current;
      const id = await ensureProjectId();
      if (!id) return false;
      await updateSettingsAsync({ soilSightProjectId: id, body, ...(klimFarmId ? { klimFarmId } : {}) });
      if (!isSameProject()) return false;
      // Recorded so neither the autosave nor a flush sends this body again; a newer pick made meanwhile is kept.
      if (settingsPayloadRef.current === payloadBeforeSave) settingsPayloadRef.current = body;
      settingsSavedRef.current = JSON.stringify(body);
      return true;
    },
    [ensureProjectId, projectEpochRef, settingsSavedRef, updateSettingsAsync]
  );

  const flushInFlightRef = useRef<Promise<boolean> | null>(null);
  // Set when a failed save blocked a switch; cleared by the next successful save or edit, so only the switch right after a failure goes through.
  const switchBlockedRef = useRef(false);

  useEffect(
    function allowBlockingAgainAfterAnEdit() {
      switchBlockedRef.current = false;
    },
    [scenarios, settings, projectRefinements]
  );

  /** True once per failed save: a save that keeps failing must not trap the user on the project. */
  const shouldBlockSwitchAfterFailedSave = useCallback(function shouldBlockSwitchAfterFailedSave(): boolean {
    if (switchBlockedRef.current) return false;
    switchBlockedRef.current = true;
    return true;
  }, []);

  // A loaded project's settings ledger is seeded by hydration; with no project yet, the first save creates one.
  const canSaveSettings = useCallback(
    function canSaveSettings(): boolean {
      return hasHydratedRef.current || !soilSightProjectIdRef.current;
    },
    [hasHydratedRef]
  );

  // Compares the saved ledgers with what the page shows, without sending anything.
  const hasUnsavedChanges = useCallback(
    function hasUnsavedChanges(): boolean {
      if (canSaveSettings() && settingsSavedRef.current !== JSON.stringify(settingsPayloadRef.current)) return true;
      if (!hasHydratedRef.current) return false;
      return (
        toCropYearsRows(scenariosRef.current).some((row) => isRowUnsaved(cropYearsSavedRef, row)) ||
        toScenarioMetaRows(scenariosRef.current).some((row) => isRowUnsaved(scenarioMetaSavedRef, row))
      );
    },
    [canSaveSettings, cropYearsSavedRef, hasHydratedRef, scenarioMetaSavedRef, settingsSavedRef]
  );

  /** What a run reads from the server (settings and crop years), to tell whether the page changed while a run was out. */
  const getRunInputsSnapshot = useCallback(function getRunInputsSnapshot(): string {
    return JSON.stringify([settingsPayloadRef.current, toCropYearsRows(scenariosRef.current)]);
  }, []);

  /** Saves what the debounced autosaves have not (edits still in their window, or failed saves); resolves false when a save failed (one toast). */
  const flushUnsavedChanges = useCallback(
    function flushUnsavedChanges(): Promise<boolean> {
      const runningFlush = flushInFlightRef.current;
      // A caller joining a running flush may have newer edits than its snapshot, so it flushes again once that settles.
      if (runningFlush) return runningFlush.then((isSaved) => (isSaved && hasUnsavedChanges() ? flushUnsavedChanges() : isSaved));
      const saveUnsavedChanges = async (): Promise<boolean> => {
        // The flush belongs to the project it started on; after a switch, the next project's state is not ours to save.
        const isFlushProject = watchProjectSwitch(projectEpochRef);
        // Each pass also sends edits made while the previous one was saving.
        for (let pass = 0; pass < SOIL_SIGHT_MAX_FLUSH_PASSES; pass += 1) {
          // In-flight autosaves land first, so an older PUT can't reach the server after this newer one.
          // eslint-disable-next-line no-await-in-loop
          const isIdle = await waitUntilIdle(isSavingRef);
          if (!isIdle) {
            showSoilSightSaveFailed(t);
            return false;
          }
          if (!isFlushProject()) return true;
          if (!hasUnsavedChanges()) break;
          // Saves finishing after a project switch must not mark the next project's ledgers as saved.
          const isSameProject = watchProjectSwitch(projectEpochRef);
          const saves: Promise<unknown>[] = [];
          const settingsBody = settingsPayloadRef.current;
          if (canSaveSettings() && settingsSavedRef.current !== JSON.stringify(settingsBody)) {
            // No project to save into is a failure; a project switch is handled after the pass.
            saves.push(
              saveSettingsNow(settingsBody).then((isSettingsSaved) => {
                if (!isSettingsSaved && isSameProject()) throw new Error("Settings were not saved");
              })
            );
          }
          // Same gate as the scenario autosaves: before hydration the ledgers don't reflect the server yet.
          if (hasHydratedRef.current) {
            const { current: currentScenarios } = scenariosRef;
            saveChangedCropYears(toCropYearsRows(currentScenarios), isSameProject).forEach(({ promise }) => saves.push(promise));
            saveChangedScenarioMeta(toScenarioMetaRows(currentScenarios), isSameProject).forEach(({ promise }) => saves.push(promise));
          }
          // eslint-disable-next-line no-await-in-loop
          const results = await Promise.allSettled(saves);
          const firstFailure = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
          if (firstFailure) {
            showSoilSightSaveFailed(t, firstFailure.reason);
            return false;
          }
          // After a switch the ledgers belong to the next project, so there is nothing left to compare against.
          if (!isSameProject()) return true;
        }
        // Edits still arriving after the last pass are unsaved, so callers must not treat the page as saved.
        if (hasUnsavedChanges()) return false;
        switchBlockedRef.current = false;
        return true;
      };
      flushInFlightRef.current = saveUnsavedChanges().finally(() => {
        flushInFlightRef.current = null;
      });
      return flushInFlightRef.current;
    },
    [
      canSaveSettings,
      hasHydratedRef,
      hasUnsavedChanges,
      isSavingRef,
      projectEpochRef,
      saveChangedCropYears,
      saveChangedScenarioMeta,
      saveSettingsNow,
      settingsSavedRef,
      t,
    ]
  );

  /** Strips and saves blank rows so every run path judges the same years; null when a save failed (toasted) or the project switched. */
  const pruneAndPersistEmptyYears = useCallback(
    async function pruneAndPersistEmptyYears(currentScenarios: ScenarioData[]): Promise<ScenarioData[] | null> {
      const pruned = withEmptyYearsPruned(currentScenarios);
      const changedScenarios = pruned.filter((scenario, index) => scenario.years.length !== currentScenarios[index]?.years.length);
      if (changedScenarios.length === 0) return currentScenarios;

      const isSameProject = watchProjectSwitch(projectEpochRef);
      const persistable = changedScenarios.filter((scenario) => isApiScenarioId(scenario.id));
      const results = await Promise.allSettled(
        persistable.map(async (scenario) => {
          const body = toCropYearsPayload(scenario.years);
          await replaceCropYearsAsync({ scenarioId: scenario.id, body });
          if (isSameProject()) cropYearsSavedRef.current.set(scenario.id, JSON.stringify(body));
        })
      );
      if (!isSameProject()) return null;
      const failedIds = new Set(persistable.filter((_, index) => results[index].status === "rejected").map((scenario) => scenario.id));
      const yearsBeforePrune = new Map(currentScenarios.map((scenario) => [scenario.id, JSON.stringify(scenario.years)]));
      const prunedYearsById = new Map(changedScenarios.map((scenario) => [scenario.id, scenario.years]));
      // Failed scenarios keep their rows (as on the server), and years edited during the save keep the edit.
      const applyPruning = (latest: ScenarioData[]) =>
        latest.map((scenario) => {
          const prunedYears = prunedYearsById.get(scenario.id);
          if (!prunedYears || failedIds.has(scenario.id) || JSON.stringify(scenario.years) !== yearsBeforePrune.get(scenario.id)) return scenario;
          return { ...scenario, years: prunedYears };
        });
      // Updated now, not on the next render: the flush right after this must not re-save the blank rows.
      scenariosRef.current = applyPruning(scenariosRef.current);
      setScenarios(applyPruning);
      if (failedIds.size > 0) {
        const firstFailure = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
        showSoilSightSaveFailed(t, firstFailure?.reason);
        return null;
      }
      return pruned;
    },
    [cropYearsSavedRef, projectEpochRef, replaceCropYearsAsync, setScenarios, t]
  );

  return {
    isSavingCount,
    isSavingRef,
    saveSettingsNow,
    setPendingSettingsPayload,
    flushUnsavedChanges,
    getRunInputsSnapshot,
    shouldBlockSwitchAfterFailedSave,
    pruneAndPersistEmptyYears,
  };
};

export type SoilSightPersistence = ReturnType<typeof useSoilSightPersistence>;
